import { Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import type { LoadProgressPayload } from '@/visualization';

export interface LoadingProgressDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Loading progress information */
    progress: LoadProgressPayload;
    /** Optional title override */
    title?: string;
    /** Optional description override */
    description?: string;
    /** Called when the user cancels — triggers navigation away and aborts the download on unmount */
    onCancel?: () => void;
}

/**
 * Simple loading dialog showing aggregate progress.
 * Displays a spinner, progress bar, and percentage.
 * When onCancel is provided, a Cancel button is shown and Escape/outside-click are allowed.
 */
export function LoadingProgressDialog({
    open,
    progress,
    title = 'Loading Visualization Data',
    description = 'Please wait while we load the case data for visualization.',
    onCancel,
}: LoadingProgressDialogProps) {
    const percentage = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

    return (
        <Dialog open={open} onOpenChange={onCancel ? (next) => !next && onCancel() : undefined}>
            <DialogContent
                className="sm:max-w-md"
                onPointerDownOutside={onCancel ? undefined : (e) => e.preventDefault()}
                onEscapeKeyDown={onCancel ? undefined : (e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        {title}
                    </DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Progress bar */}
                    <div className="space-y-2">
                        <Progress value={percentage} className="h-2" />
                        <div className="flex justify-between text-sm text-muted-foreground">
                            <span>
                                {progress.loaded} of {progress.total} items loaded
                            </span>
                            <span>{percentage}%</span>
                        </div>
                    </div>

                    {/* Status message */}
                    <p className="text-center text-sm text-muted-foreground">
                        {progress.isComplete ? 'Processing data...' : 'Loading data...'}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
